import type { OnboardingDraftV1 } from "@repo/types";
import { useCallback, useMemo, useState } from "react";
import { LoginAuthPanel, useLoginController } from "~/features/login";
import {
  createOnboardingV1AccountDataSource,
  type OnboardingV1AccountDataSource,
  type OnboardingV1ClaimedDraft,
} from "~/features/start/state/onboarding-v1-account-data-source";
import { m as messages } from "~/lib/i18n";
import { OnboardingV1RetryState } from "./onboarding-v1-retry-state";

interface OnboardingV1AccountStepProps {
  readonly dataSource?: OnboardingV1AccountDataSource;
  readonly draft: OnboardingDraftV1;
  readonly onClaimed: (draft: OnboardingV1ClaimedDraft) => void;
  readonly onStarterInvalid: () => void;
  readonly onUsernameConflict: () => void;
}

export function OnboardingV1AccountStep(props: OnboardingV1AccountStepProps) {
  const { draft, onClaimed, onStarterInvalid, onUsernameConflict } = props;
  const dataSource = useMemo(
    () => props.dataSource ?? createOnboardingV1AccountDataSource(),
    [props.dataSource],
  );
  const [claimFailed, setClaimFailed] = useState(false);

  const claimDraft = useCallback(async () => {
    setClaimFailed(false);
    try {
      const result = await dataSource.claimDraft(draft);
      if (result.status === "username_conflict") {
        onUsernameConflict();
        return;
      }
      if (result.status === "starter_invalid") {
        onStarterInvalid();
        return;
      }
      onClaimed(result.draft);
    } catch {
      setClaimFailed(true);
    }
  }, [dataSource, draft, onClaimed, onStarterInvalid, onUsernameConflict]);

  const controller = useLoginController({ onAuthenticated: claimDraft });

  return (
    <div
      className="onboarding-v1__account-step"
      data-testid="onboarding-v1-step-account"
    >
      <h1>{messages.onboarding_v1_account_heading()}</h1>
      {claimFailed ? (
        <OnboardingV1RetryState
          className="onboarding-v1__account-error"
          onRetry={() => void claimDraft()}
        />
      ) : (
        <LoginAuthPanel
          appearance="compact"
          className="onboarding-v1__account-panel"
          controller={controller}
          methodsDescription=""
          methodsHeading={messages.onboarding_v1_account_panel_heading()}
        />
      )}
    </div>
  );
}
